import { useEffect, useState, useSyncExternalStore } from "react";
import { getSnapshot, subscribe } from "./services/session.service.js";
import { LoginPage } from "./pages/Login.page.js";
import { SignUpPage } from "./pages/SignUp.page.js";
import { SignedInPage } from "./pages/SignedIn.page.js";
import { AccountDeletedDialog } from "./components/AccountDeletedDialog.comp.js";

/**
 * Session state is the only router this app has for now, with a single local flag choosing between
 * the two signed-out views. When the companion surfaces land, this becomes a real route tree behind
 * the same signed-in gate — at which point signup/login become routes rather than a boolean.
 *
 * `useSyncExternalStore` over the session's plain listener set — no state library needed for a
 * single value, and it keeps the session usable from non-React code (the SDK's token hook).
 */
export function App() {
  const session = useSyncExternalStore(subscribe, getSnapshot);
  const [showSignUp, setShowSignUp] = useState(false);
  /**
   * Why we're showing signup, when the user didn't ask for it — today only "a social login found no
   * account". It lives here rather than in `SignUpPage` because the reason is decided on the LOGIN
   * page, and this component is the only thing both pages have in common.
   */
  const [signUpNotice, setSignUpNotice] = useState<string | undefined>(undefined);

  // Reset the signed-out view to LOGIN once a session exists, so the next sign-out lands there.
  // Without this the flag survives the whole session: sign up → log out drops you back on the
  // signup form for the account you just created, which reads as "it didn't work". The live e2e
  // caught it — and only failed in the tests that DIDN'T reload, since a reload resets React state
  // and hid the bug.
  useEffect(() => {
    if (session.status !== "signedIn") return;
    setShowSignUp(false);
    setSignUpNotice(undefined);
  }, [session.status]);

  if (session.status === "loading") {
    return (
      <main className="card" data-testid="app-loading">
        <h1 className="logo">roo</h1>
        <p className="hint">Loading…</p>
      </main>
    );
  }

  if (session.status === "signedIn") {
    return <SignedInPage user={session.user} />;
  }

  const signedOutView = showSignUp ? (
    <SignUpPage
      notice={signUpNotice}
      onBackToLogin={() => {
        setSignUpNotice(undefined);
        setShowSignUp(false);
      }}
    />
  ) : (
    <LoginPage
      onCreateAccount={(opts) => {
        setSignUpNotice(opts?.notice);
        setShowSignUp(true);
      }}
    />
  );

  // Rendered OVER the signed-out view rather than instead of it, so dismissing the dialog reveals
  // the login page already behind it — no second transition, and no blank frame.
  return (
    <>
      {signedOutView}
      {session.status === "accountDeleted" ? <AccountDeletedDialog /> : null}
    </>
  );
}
